import { ethers } from "ethers";
import { useMemo } from "react";

import { isMarketEnabled } from "config/markets";
import { MARKETS } from "config/markets";
import { convertTokenAddress, getToken, getTokensMap } from "sdk/configs/tokens";

import { MarketsData } from "./types";
import { getMarketFullName } from "./utils";

export type MarketsResult = {
  marketsData?: MarketsData;
  marketsAddresses?: string[];
  error?: Error | undefined;
};

export function useMarkets(chainId: number): MarketsResult {
  return useMemo(() => {
    const markets = MARKETS[chainId];
    const tokensMap = getTokensMap(chainId);

    if (!markets) {
      console.log(`DEBUG: No markets found for chainId ${chainId}`); // verification
      return {
        marketsData: {},
        marketsAddresses: [],
        error: new Error(`Static markets data for chain ${chainId} not found`),
      };
    }
    // console.log(`DEBUG: Found ${Object.keys(markets).length} markets for chainId ${chainId}`); // verification
    // console.log(`DEBUG: Market Keys:`, Object.keys(markets)); // verification

    return Object.values(markets).reduce(
      (acc: MarketsResult, enabledMarketConfig) => {
        const market = enabledMarketConfig;

        if (!isMarketEnabled(chainId, market.marketTokenAddress)) {
          // console.log(`useMarkets: Market ${market.marketTokenAddress} disabled`);
          return acc;
        }

        const isSpotOnly = market.indexTokenAddress === ethers.ZeroAddress;

        try {
          let indexTokenAddress: string | undefined;

          if (!isSpotOnly) {
            indexTokenAddress = convertTokenAddress(chainId, market.indexTokenAddress, "native");
            if (!indexTokenAddress || !tokensMap[indexTokenAddress]) {
              // eslint-disable-next-line no-console
              console.warn(
                `useMarkets: Missing index token ${indexTokenAddress} for market ${market.marketTokenAddress}`
              );
              return acc;
            }
          }

          if (!tokensMap[market.longTokenAddress] || !tokensMap[market.shortTokenAddress]) {
            // eslint-disable-next-line no-console
            console.warn(
              `useMarkets: Missing long/short token for market ${market.marketTokenAddress}. Long: ${market.longTokenAddress} (${!!tokensMap[market.longTokenAddress]}), Short: ${market.shortTokenAddress} (${!!tokensMap[market.shortTokenAddress]})`
            );
            return acc;
          }

          const indexToken = isSpotOnly ? undefined : getToken(chainId, indexTokenAddress!);
          const longToken = getToken(chainId, market.longTokenAddress);
          const shortToken = getToken(chainId, market.shortTokenAddress);

          const isSameCollaterals = market.longTokenAddress === market.shortTokenAddress;

          const name = getMarketFullName({ indexToken: indexToken as any, longToken, shortToken, isSpotOnly });

          acc.marketsAddresses!.push(market.marketTokenAddress);
          acc.marketsData![market.marketTokenAddress] = {
            ...market,
            isSameCollaterals,
            isSpotOnly,
            name,
            data: "",
          };
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn("unsupported market", e);
        }

        return acc;
      },
      { marketsData: {}, marketsAddresses: [] }
    );
  }, [chainId]);
}
